import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import {
    Button, Container, Table, Spinner, Alert, Modal, Form,
    Row, Col, Badge, Card
} from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import { FaPlus, FaEdit, FaTrash, FaEye, FaCopy, FaExchangeAlt, FaTable, FaTh } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import api, { getClients, createClient } from '../services/api';
import { trackAction } from '../services/analytics';
import usePermissions, { PERMISSIONS } from '../hooks/usePermissions';
import AuthContext from '../context/AuthContext';
import Pagination from '../components/Pagination';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_VARIANTS = {
    draft: 'secondary',
    sent: 'info',
    accepted: 'success',
    rejected: 'danger',
    expired: 'warning',
    converted: 'primary',
};

const formatCurrency = (num) =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(num || 0);

// La cotización perdió vigencia si su fecha de validez ya pasó (comparando por día, no por hora).
const isPastDate = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr) < today;
};

// "Vencida" solo aplica mientras la decisión sigue abierta: ya aceptada o convertida no cuenta.
const isQuoteExpired = (quote) =>
    !!quote.valid_until &&
    !['converted', 'accepted'].includes(quote.status) &&
    isPastDate(quote.valid_until);

// ─── QuoteFormModal ───────────────────────────────────────────────────────────

const EMPTY_ITEM = { product_name: '', quantity: 1, unit_price: 0 };

// Vigencia por defecto: hoy + 15 días (formato YYYY-MM-DD para el input date).
const DEFAULT_VALIDITY_DAYS = 15;
const defaultValidUntil = () => {
    const d = new Date();
    d.setDate(d.getDate() + DEFAULT_VALIDITY_DAYS);
    return d.toISOString().slice(0, 10);
};

const QuoteFormModal = ({ show, onHide, onSaved, editingQuote, clientList, clientOptions, onClientCreated }) => {
    const { t } = useTranslation();
    const [form, setForm] = useState({
        client_id: null,
        client_name: '',
        client_phone: '',
        client_address: '',
        valid_until: defaultValidUntil(),
        observations: '',
        items: [{ ...EMPTY_ITEM }],
    });
    const [showClientDetails, setShowClientDetails] = useState(false);
    const [saveClientData, setSaveClientData] = useState(false);
    const [selectedClientOption, setSelectedClientOption] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!show) return;
        if (editingQuote) {
            const existingOption = editingQuote.client_id
                ? clientOptions.find(o => o.value === editingQuote.client_id) || null
                : null;
            setSelectedClientOption(existingOption);
            setShowClientDetails(!editingQuote.client_id && !!(editingQuote.client_name));
            setForm({
                client_id: editingQuote.client_id || null,
                client_name: editingQuote.client_name || '',
                client_phone: editingQuote.client_phone || '',
                client_address: editingQuote.client_address || '',
                valid_until: editingQuote.valid_until ? editingQuote.valid_until.substring(0, 10) : '',
                observations: editingQuote.observations || '',
                items: (editingQuote.items || []).map(i => ({
                    product_name: i.product_name,
                    quantity: parseFloat(i.quantity),
                    unit_price: parseFloat(i.unit_price),
                })),
            });
        } else {
            setSelectedClientOption(null);
            setShowClientDetails(false);
            setSaveClientData(false);
            setForm({
                client_id: null,
                client_name: '',
                client_phone: '',
                client_address: '',
                valid_until: defaultValidUntil(),
                observations: '',
                items: [{ ...EMPTY_ITEM }],
            });
        }
        setError('');
    }, [show, editingQuote, clientOptions]);

    const handleClientChange = (selectedOption, actionMeta) => {
        if (actionMeta.action === 'select-option') {
            setShowClientDetails(false);
            setSaveClientData(false);
            setSelectedClientOption(selectedOption);
            setForm(prev => ({
                ...prev,
                client_id: selectedOption.value,
                client_name: selectedOption.name || '',
                client_phone: selectedOption.phone || '',
                client_address: selectedOption.address || '',
            }));
        } else if (actionMeta.action === 'create-option') {
            setShowClientDetails(true);
            setSelectedClientOption(selectedOption);
            setForm(prev => ({
                ...prev,
                client_id: null,
                client_name: selectedOption.value,
                client_phone: '',
                client_address: '',
            }));
        } else if (actionMeta.action === 'clear') {
            setShowClientDetails(false);
            setSaveClientData(false);
            setSelectedClientOption(null);
            setForm(prev => ({
                ...prev,
                client_id: null,
                client_name: '',
                client_phone: '',
                client_address: '',
            }));
        }
    };

    const updateItem = (idx, field, value) => {
        setForm(prev => {
            const items = [...prev.items];
            items[idx] = { ...items[idx], [field]: value };
            return { ...prev, items };
        });
    };

    const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, { ...EMPTY_ITEM }] }));

    const removeItem = (idx) => {
        setForm(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== idx),
        }));
    };

    const totalAmount = form.items.reduce(
        (sum, i) => sum + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0),
        0
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (form.items.length === 0) {
            setError(t('quotesPage.noItems'));
            return;
        }
        if (!form.valid_until) {
            setError(t('quotesPage.validUntilRequired'));
            return;
        }
        setSaving(true);
        setError('');
        try {
            const payload = {
                client_id: form.client_id || null,
                client_name: form.client_name || null,
                client_phone: form.client_phone || null,
                client_address: form.client_address || null,
                valid_until: form.valid_until || null,
                observations: form.observations || null,
                items: form.items,
            };
            if (editingQuote) {
                await api.put(`/quotes/${editingQuote.id}`, payload);
            } else {
                await api.post('/quotes', payload);
            }
            // Telemetría (tarea 004): sin PII (items_count, no datos del cliente).
            trackAction(editingQuote ? 'quote_updated' : 'quote_created', { items_count: form.items.length });

            // Guardar cliente en BD si el checkbox está marcado
            if (saveClientData && !editingQuote && !form.client_id && form.client_phone?.trim()) {
                const phoneExists = clientList.find(c => c.phone === form.client_phone.trim());
                if (!phoneExists) {
                    try {
                        const newClientRes = await createClient({
                            name: form.client_name,
                            phone: form.client_phone,
                            address: form.client_address,
                        });
                        onClientCreated(newClientRes.data);
                        toast.success(t('quotesPage.clientSaved'));
                    } catch {
                        toast.error(t('quotesPage.clientSaveFailed'));
                    }
                }
            }

            toast.success(t('quotesPage.saveSuccess'));
            onSaved();
        } catch (err) {
            trackAction('quote_save_failed', { is_edit: !!editingQuote });
            setError(err.response?.data?.message || t('quotesPage.saveError'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    {editingQuote ? t('quotesPage.modal.editTitle') : t('quotesPage.modal.createTitle')}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    {/* Selector de cliente */}
                    <Form.Group className="mb-3">
                        <Form.Label>{t('quotesPage.searchClient')}</Form.Label>
                        <CreatableSelect
                            isClearable
                            options={clientOptions}
                            value={selectedClientOption}
                            onChange={handleClientChange}
                            placeholder={t('quotesPage.searchClientPlaceholder')}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </Form.Group>

                    {/* Checkbox guardar cliente */}
                    {!form.client_id && showClientDetails && (
                        <div className="mb-3 p-3 rounded d-flex align-items-center" style={{ backgroundColor: 'var(--ev-bg-brand-subtle)' }}>
                            <Form.Check
                                type="checkbox"
                                id="save-quote-client"
                                label={<span className="fw-bold text-dark">{t('quotesPage.saveClientData')}</span>}
                                checked={saveClientData}
                                onChange={e => setSaveClientData(e.target.checked)}
                                className="mb-0 w-100"
                            />
                        </div>
                    )}

                    {/* Campos de cliente para nuevo cliente o edición sin client_id */}
                    {!form.client_id && (showClientDetails || (editingQuote && !editingQuote.client_id && editingQuote.client_name)) && (
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Label>{t('quotesPage.clientName')}</Form.Label>
                                <Form.Control
                                    value={form.client_name}
                                    onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))}
                                    placeholder={t('quotesPage.clientName')}
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label>{t('quotesPage.clientPhone')}</Form.Label>
                                <Form.Control
                                    value={form.client_phone}
                                    onChange={e => setForm(p => ({ ...p, client_phone: e.target.value }))}
                                    placeholder={t('quotesPage.clientPhone')}
                                />
                            </Col>
                            <Col md={12} className="mt-2">
                                <Form.Label>{t('quotesPage.clientAddress')}</Form.Label>
                                <Form.Control
                                    value={form.client_address}
                                    onChange={e => setForm(p => ({ ...p, client_address: e.target.value }))}
                                    placeholder={t('quotesPage.clientAddress')}
                                />
                            </Col>
                        </Row>
                    )}

                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Label>
                                {t('quotesPage.validUntil')} <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="date"
                                value={form.valid_until}
                                onChange={e => setForm(p => ({ ...p, valid_until: e.target.value }))}
                                required
                            />
                            <Form.Text className="text-muted">
                                {t('quotesPage.validUntilHelp')}
                            </Form.Text>
                        </Col>
                    </Row>

                    <Form.Label className="fw-bold mt-2">{t('quotesPage.items')}</Form.Label>
                    <div className="mb-2">
                        {form.items.map((item, idx) => (
                            <Row key={idx} className="mb-2 align-items-end">
                                <Col xs={12} md={5}>
                                    <Form.Label className="d-block">{t('quotesPage.productName')}</Form.Label>
                                    <Form.Control
                                        value={item.product_name}
                                        onChange={e => updateItem(idx, 'product_name', e.target.value)}
                                        placeholder={t('quotesPage.productName')}
                                        required
                                    />
                                </Col>
                                <Col xs={4} md={2}>
                                    <Form.Label>{t('quotesPage.quantity')}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        value={item.quantity}
                                        onChange={e => updateItem(idx, 'quantity', e.target.value)}
                                        required
                                    />
                                </Col>
                                <Col xs={5} md={3}>
                                    <Form.Label>{t('quotesPage.unitPrice')}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={item.unit_price}
                                        onChange={e => updateItem(idx, 'unit_price', e.target.value)}
                                        required
                                    />
                                </Col>
                                <Col xs={3} md={2} className="d-flex align-items-end">
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => removeItem(idx)}
                                        disabled={form.items.length === 1}
                                    >
                                        {t('quotesPage.removeItem')}
                                    </Button>
                                </Col>
                            </Row>
                        ))}
                    </div>
                    <Button variant="outline-primary" size="sm" onClick={addItem} className="w-100 mb-3">
                        <FaPlus size={11} className="me-1" />
                        {t('quotesPage.addItem')}
                    </Button>
                    <div className="d-flex justify-content-end align-items-center mb-3">
                        <h5 className="me-3 mb-0">{t('quotesPage.totalAmount')}</h5>
                        <h4 className="fw-bold mb-0 text-primary">{formatCurrency(totalAmount)}</h4>
                    </div>

                    <Form.Label>{t('quotesPage.observations')}</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={form.observations}
                        onChange={e => setForm(p => ({ ...p, observations: e.target.value }))}
                        placeholder={t('quotesPage.observations')}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        {t('quotesPage.modal.cancel')}
                    </Button>
                    <Button variant="primary" type="submit" disabled={saving}>
                        {saving ? t('quotesPage.modal.saving') : t('quotesPage.modal.save')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

// ─── ConvertModal ─────────────────────────────────────────────────────────────

const ConvertModal = ({ show, onHide, quote, onConverted }) => {
    const { t } = useTranslation();
    const [paymentMode, setPaymentMode] = useState('total');
    const [paidAmount, setPaidAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [stockWarnings, setStockWarnings] = useState([]);
    const [previewLoaded, setPreviewLoaded] = useState(false);
    const [converting, setConverting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!show || !quote) return;
        setPaymentMode('total');
        setPaidAmount('');
        setPaymentMethod('Cash');
        setError('');
        setPreviewLoaded(false);

        api.get(`/quotes/${quote.id}/convert-preview`)
            .then(res => {
                setStockWarnings(res.data.stock_warnings || []);
                setPreviewLoaded(true);
            })
            .catch(() => setPreviewLoaded(true));
    }, [show, quote]);

    const handleConvert = async () => {
        setConverting(true);
        setError('');
        try {
            await api.post(`/quotes/${quote.id}/convert`, {
                payment_mode: paymentMode,
                paid_amount: paymentMode === 'partial' ? parseFloat(paidAmount) || 0 : 0,
                payment_method: paymentMode === 'pending' ? null : paymentMethod,
            });
            trackAction('quote_converted_to_sale', { payment_mode: paymentMode });
            toast.success(t('quotesPage.convertSuccess'));
            onConverted();
        } catch (err) {
            trackAction('quote_convert_failed');
            setError(err.response?.data?.message || t('quotesPage.convertError'));
        } finally {
            setConverting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>{t('quotesPage.modal.convertTitle')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!previewLoaded && <div className="text-center py-3"><Spinner animation="border" /></div>}

                {previewLoaded && stockWarnings.length > 0 && (
                    <Alert variant="warning">
                        <strong>{t('quotesPage.stockWarning')}</strong>
                        <p className="mb-1 mt-1">{t('quotesPage.stockWarningDetail')}</p>
                        <ul className="mb-1">
                            {stockWarnings.map((w, i) => (
                                <li key={i}>
                                    {w.product_name}: {t('quotesPage.quantity')} {w.requested}, {t('quotesPage.validUntil')} {w.available}
                                </li>
                            ))}
                        </ul>
                        <small>{t('quotesPage.stockWarningProceed')}</small>
                    </Alert>
                )}

                {previewLoaded && stockWarnings.length === 0 && (
                    <Alert variant="success" className="py-2">
                        <small>{t('quotesPage.noStockWarnings')}</small>
                    </Alert>
                )}

                {error && <Alert variant="danger">{error}</Alert>}

                {previewLoaded && (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t('quotesPage.paymentMode')}</Form.Label>
                            <Form.Select value={paymentMode} onChange={e => setPaymentMode(e.target.value)}>
                                <option value="total">{t('quotesPage.paymentModeTotal')}</option>
                                <option value="partial">{t('quotesPage.paymentModePartial')}</option>
                                <option value="pending">{t('quotesPage.paymentModePending')}</option>
                            </Form.Select>
                        </Form.Group>

                        {paymentMode === 'partial' && (
                            <Form.Group className="mb-3">
                                <Form.Label>{t('quotesPage.paidAmount')}</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={paidAmount}
                                    onChange={e => setPaidAmount(e.target.value)}
                                    max={quote?.total_amount}
                                />
                            </Form.Group>
                        )}

                        {paymentMode !== 'pending' && (
                            <Form.Group className="mb-3">
                                <Form.Label>{t('quotesPage.paymentMethod')}</Form.Label>
                                <Form.Select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                                    <option value="Cash">Efectivo</option>
                                    <option value="Card">Tarjeta</option>
                                    <option value="Transfer">Transferencia</option>
                                    <option value="Nequi">Nequi</option>
                                </Form.Select>
                            </Form.Group>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>{t('quotesPage.modal.cancel')}</Button>
                <Button
                    variant="success"
                    onClick={handleConvert}
                    disabled={converting || !previewLoaded}
                >
                    {converting ? t('quotesPage.modal.converting') : t('quotesPage.modal.confirm')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// ─── QuotesPage ───────────────────────────────────────────────────────────────

const QuotesPage = () => {
    const { user } = useContext(AuthContext);
    const { hasPermission } = usePermissions(user);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Lista de clientes
    const [clientList, setClientList] = useState([]);
    const clientOptions = useMemo(
        () => clientList.map(c => ({
            value: c.client_id,
            label: `${c.name} (${c.phone || t('quotesPage.noPhone', 'Sin teléfono')})`,
            ...c,
        })),
        [clientList, t]
    );

    // Filtros
    const [filterStatus, setFilterStatus] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');

    // Modales
    const [showFormModal, setShowFormModal] = useState(false);
    const [editingQuote, setEditingQuote] = useState(null);
    const [showConvertModal, setShowConvertModal] = useState(false);
    const [convertingQuote, setConvertingQuote] = useState(null);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('quotesViewMode') || 'table');

    const toggleViewMode = (mode) => {
        setViewMode(mode);
        localStorage.setItem('quotesViewMode', mode);
    };

    const canCreate = hasPermission(PERMISSIONS.CREATE_QUOTES);
    const canEdit = hasPermission(PERMISSIONS.EDIT_QUOTES);
    const canDelete = hasPermission(PERMISSIONS.DELETE_QUOTES);

    // Cargar clientes al montar
    useEffect(() => {
        getClients({ pagination: false })
            .then(res => setClientList(res.data))
            .catch(() => {});
    }, []);

    const fetchQuotes = useCallback(async (currentPage = page) => {
        setLoading(true);
        setError('');
        try {
            const params = { page: currentPage, limit: 10 };
            if (filterStatus) params.status = filterStatus;
            if (filterDateFrom) params.startDate = filterDateFrom;
            if (filterDateTo) params.endDate = filterDateTo;

            const res = await api.get('/quotes', { params });
            setQuotes(res.data.quotes);
            setTotalPages(res.data.totalPages);
        } catch (err) {
            setError(t('quotesPage.loadError'));
        } finally {
            setLoading(false);
        }
    }, [page, filterStatus, filterDateFrom, filterDateTo, t]);

    useEffect(() => {
        fetchQuotes(page);
    }, [page, fetchQuotes]);

    const handleFilter = (e) => {
        e.preventDefault();
        setPage(1);
        fetchQuotes(1);
    };

    const handleDelete = async (quote) => {
        const result = await Swal.fire({
            title: t('areYouSure'),
            text: t('cantRevert'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('yesDelete'),
            cancelButtonText: t('cancel'),
        });
        if (!result.isConfirmed) return;
        try {
            await api.delete(`/quotes/${quote.id}`);
            trackAction('quote_deleted');
            toast.success(t('quotesPage.deleteSuccess'));
            fetchQuotes(page);
        } catch {
            toast.error(t('quotesPage.deleteError'));
        }
    };

    const handleStatusAction = async (quote, action) => {
        // Aceptar una cotización vencida es válido, pero se avisa antes: el precio pudo cambiar.
        if (action === 'accept' && isQuoteExpired(quote)) {
            const result = await Swal.fire({
                title: t('quotesPage.acceptExpiredTitle'),
                text: t('quotesPage.acceptExpiredText', {
                    date: new Date(quote.valid_until).toLocaleDateString(),
                }),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('quotesPage.acceptExpiredConfirm'),
                cancelButtonText: t('cancel'),
            });
            if (!result.isConfirmed) return;
        }
        try {
            await api.patch(`/quotes/${quote.id}/status`, { action });
            trackAction('quote_status_changed', { action }); // action: sent/accepted/rejected/…
            toast.success(t('quotesPage.statusUpdateSuccess'));
            fetchQuotes(page);
        } catch (err) {
            toast.error(err.response?.data?.message || t('quotesPage.statusUpdateError'));
        }
    };

    const copyPublicLink = (quote) => {
        const url = `${window.location.origin}/quote/${quote.public_id}`;
        navigator.clipboard.writeText(url).then(() => {
            toast.success(t('quotesPage.actions.linkCopied'));
        });
    };

    const openNew = () => {
        setEditingQuote(null);
        setShowFormModal(true);
    };

    const openEdit = (quote) => {
        setEditingQuote(quote);
        setShowFormModal(true);
    };

    const openConvert = (quote) => {
        setConvertingQuote(quote);
        setShowConvertModal(true);
    };

    const handleSaved = () => {
        setShowFormModal(false);
        fetchQuotes(page);
    };

    const handleConverted = () => {
        setShowConvertModal(false);
        fetchQuotes(page);
    };

    return (
        <Container className="py-4">
            <Row className="align-items-center mb-4">
                <Col>
                    <h2>{t('quotesPage.title')}</h2>
                    <p className="text-muted mb-0">{t('quotesPage.subtitle')}</p>
                </Col>
                <Col xs="auto">
                    {canCreate && (
                        <Button
                            variant="primary"
                            onClick={openNew}
                        >
                            <FaPlus className="me-2" /> {t('quotesPage.newQuote')}
                        </Button>
                    )}
                </Col>
            </Row>

            {/* Filtros */}
            <Form onSubmit={handleFilter} className="mb-3">
                <Row className="g-2 align-items-end">
                    <Col xs={12} md={3}>
                        <Form.Select
                            value={filterStatus}
                            onChange={e => setFilterStatus(e.target.value)}
                        >
                            <option value="">{t('quotesPage.filters.all')}</option>
                            {['draft', 'sent', 'accepted', 'rejected', 'expired', 'converted'].map(s => (
                                <option key={s} value={s}>{t(`quotesPage.status.${s}`)}</option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col xs={6} md={3}>
                        <Form.Control
                            type="date"
                            value={filterDateFrom}
                            onChange={e => setFilterDateFrom(e.target.value)}
                            placeholder={t('quotesPage.filters.dateFrom')}
                        />
                    </Col>
                    <Col xs={6} md={3}>
                        <Form.Control
                            type="date"
                            value={filterDateTo}
                            onChange={e => setFilterDateTo(e.target.value)}
                            placeholder={t('quotesPage.filters.dateTo')}
                        />
                    </Col>
                    <Col xs={12} md={3}>
                        <Button type="submit" variant="outline-secondary" className="w-100">
                            {t('quotesPage.filters.all')}
                        </Button>
                    </Col>
                </Row>
            </Form>

            {error && <Alert variant="danger">{error}</Alert>}

            {/* View Mode Toggle */}
            <div className="d-flex justify-content-end align-items-center mb-3">
                <div
                    className="sales-view-toggle"
                    role="group"
                    aria-label={t('quotesPage.viewToggle.label', 'Cambiar vista')}
                >
                    <button
                        id="quotes-view-table"
                        className={`sales-view-toggle__btn${viewMode === 'table' ? ' sales-view-toggle__btn--active' : ''}`}
                        onClick={() => toggleViewMode('table')}
                        aria-pressed={viewMode === 'table'}
                        title={t('quotesPage.viewToggle.tableView', 'Vista tabla')}
                        aria-label={t('quotesPage.viewToggle.tableView', 'Vista tabla')}
                    >
                        <FaTable aria-hidden="true" />
                        <span className="sales-view-toggle__label">{t('quotesPage.viewToggle.tableView', 'Tabla')}</span>
                    </button>
                    <button
                        id="quotes-view-cards"
                        className={`sales-view-toggle__btn${viewMode === 'cards' ? ' sales-view-toggle__btn--active' : ''}`}
                        onClick={() => toggleViewMode('cards')}
                        aria-pressed={viewMode === 'cards'}
                        title={t('quotesPage.viewToggle.cardsView', 'Vista tarjetas')}
                        aria-label={t('quotesPage.viewToggle.cardsView', 'Vista tarjetas')}
                    >
                        <FaTh aria-hidden="true" />
                        <span className="sales-view-toggle__label">{t('quotesPage.viewToggle.cardsView', 'Tarjetas')}</span>
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : viewMode === 'table' ? (
                <div className="modern-table-wrapper mt-1">
                    <Table hover responsive className="modern-table">
                        <thead>
                            <tr>
                                <th>{t('quotesPage.table.quoteNumber')}</th>
                                <th>{t('quotesPage.table.client')}</th>
                                <th>{t('quotesPage.table.total')}</th>
                                <th>{t('quotesPage.table.status')}</th>
                                <th>{t('quotesPage.table.validUntil')}</th>
                                <th>{t('quotesPage.table.createdAt')}</th>
                                <th>{t('quotesPage.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {quotes.length === 0 ? (
                                <tr><td colSpan={7} className="text-center py-4 text-muted">{t('quotesPage.emptyState')}</td></tr>
                            ) : quotes.map(quote => (
                                <tr key={quote.id}>
                                    <td>#{quote.quote_number}</td>
                                    <td>{quote.client_name || '—'}</td>
                                    <td>{formatCurrency(quote.total_amount)}</td>
                                    <td>
                                        <Badge bg={STATUS_VARIANTS[quote.status] || 'secondary'}>
                                            {t(`quotesPage.status.${quote.status}`, quote.status)}
                                        </Badge>
                                    </td>
                                    <td>
                                        {quote.valid_until ? (
                                            <div className={isQuoteExpired(quote) ? 'text-danger fw-semibold' : undefined}>
                                                <div>{new Date(quote.valid_until).toLocaleDateString()}</div>
                                                {isQuoteExpired(quote) && (
                                                    <Badge bg="danger" className="mt-1">
                                                        {t('quotesPage.expired')}
                                                    </Badge>
                                                )}
                                            </div>
                                        ) : '—'}
                                    </td>
                                    <td>{new Date(quote.created_at).toLocaleDateString()}</td>
                                    <td>
                                        <div className="btn-actions">
                                            {/* Ver (página pública) */}
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                title={t('quotesPage.actions.view')}
                                                onClick={() => navigate(`/quote/${quote.public_id}`)}
                                            >
                                                <FaEye />
                                            </Button>

                                            {/* Copiar enlace */}
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                title={t('quotesPage.actions.copyLink')}
                                                onClick={() => copyPublicLink(quote)}
                                            >
                                                <FaCopy />
                                            </Button>

                                            {/* Editar — solo en draft */}
                                            {canEdit && quote.status === 'draft' && (
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    title={t('quotesPage.actions.edit')}
                                                    onClick={() => openEdit(quote)}
                                                >
                                                    <FaEdit />
                                                </Button>
                                            )}

                                            {/* Acciones de estado */}
                                            {canEdit && quote.status === 'draft' && (
                                                <Button variant="outline-info" size="sm" onClick={() => handleStatusAction(quote, 'send')}>
                                                    {t('quotesPage.actions.send')}
                                                </Button>
                                            )}
                                            {canEdit && quote.status === 'sent' && (
                                                <>
                                                    <Button variant="outline-success" size="sm" onClick={() => handleStatusAction(quote, 'accept')}>
                                                        {t('quotesPage.actions.accept')}
                                                    </Button>
                                                    <Button variant="outline-danger" size="sm" onClick={() => handleStatusAction(quote, 'reject')}>
                                                        {t('quotesPage.actions.reject')}
                                                    </Button>
                                                </>
                                            )}
                                            {canEdit && (quote.status === 'rejected' || quote.status === 'expired') && (
                                                <Button variant="outline-secondary" size="sm" onClick={() => handleStatusAction(quote, 'reopen')}>
                                                    {t('quotesPage.actions.reopen')}
                                                </Button>
                                            )}
                                            {canEdit && quote.status === 'accepted' && (
                                                <Button variant="success" size="sm" onClick={() => openConvert(quote)}>
                                                    <FaExchangeAlt className="me-1" />
                                                    {t('quotesPage.actions.convert')}
                                                </Button>
                                            )}

                                            {/* Eliminar */}
                                            {canDelete && quote.status !== 'converted' && (
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    title={t('quotesPage.actions.delete')}
                                                    onClick={() => handleDelete(quote)}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            ) : (
                /* ── Cards view ── */
                <div>
                    {quotes.length === 0 ? (
                        <div className="text-center py-5 text-muted">{t('quotesPage.emptyState')}</div>
                    ) : (
                        <div className="sales-cards-grid">
                            {quotes.map(quote => (
                                <div key={quote.id} className={`sale-card${isQuoteExpired(quote) ? ' sale-card--pending' : ''}`}>
                                    <div className="sale-card__header">
                                        <div className="sale-card__invoice">
                                            <span className="sale-card__invoice-label">#{quote.quote_number}</span>
                                        </div>
                                        <span className={`badge bg-${STATUS_VARIANTS[quote.status] || 'secondary'} sale-card__badge`}>
                                            {t(`quotesPage.status.${quote.status}`, quote.status)}
                                        </span>
                                    </div>

                                    <div className="sale-card__client">
                                        <span className="sale-card__client-name">{quote.client_name || '—'}</span>
                                    </div>

                                    <div className="sale-card__amounts">
                                        <div className="sale-card__amount-item">
                                            <span className="sale-card__amount-label">{t('quotesPage.table.total')}</span>
                                            <span className="sale-card__amount-value sale-card__amount-value--total">{formatCurrency(quote.total_amount)}</span>
                                        </div>
                                    </div>

                                    <div className="sale-card__dates">
                                        <div className="sale-card__date-item">
                                            <span className="sale-card__date-label">{t('quotesPage.table.createdAt')}</span>
                                            <span className="sale-card__date-value">{new Date(quote.created_at).toLocaleDateString()}</span>
                                        </div>
                                        <div className="sale-card__date-item">
                                            <span className="sale-card__date-label">{t('quotesPage.table.validUntil')}</span>
                                            <span className="sale-card__date-value">
                                                {quote.valid_until ? new Date(quote.valid_until).toLocaleDateString() : '—'}
                                                {isQuoteExpired(quote) && (
                                                    <Badge bg="danger" className="ms-1">{t('quotesPage.expired')}</Badge>
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="sale-card__actions btn-actions">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            title={t('quotesPage.actions.view')}
                                            onClick={() => navigate(`/quote/${quote.public_id}`)}
                                        >
                                            <FaEye />
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            title={t('quotesPage.actions.copyLink')}
                                            onClick={() => copyPublicLink(quote)}
                                        >
                                            <FaCopy />
                                        </Button>
                                        {canEdit && quote.status === 'draft' && (
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                title={t('quotesPage.actions.edit')}
                                                onClick={() => openEdit(quote)}
                                            >
                                                <FaEdit />
                                            </Button>
                                        )}
                                        {canEdit && quote.status === 'draft' && (
                                            <Button variant="outline-info" size="sm" onClick={() => handleStatusAction(quote, 'send')}>
                                                {t('quotesPage.actions.send')}
                                            </Button>
                                        )}
                                        {canEdit && quote.status === 'sent' && (
                                            <>
                                                <Button variant="outline-success" size="sm" onClick={() => handleStatusAction(quote, 'accept')}>
                                                    {t('quotesPage.actions.accept')}
                                                </Button>
                                                <Button variant="outline-danger" size="sm" onClick={() => handleStatusAction(quote, 'reject')}>
                                                    {t('quotesPage.actions.reject')}
                                                </Button>
                                            </>
                                        )}
                                        {canEdit && (quote.status === 'rejected' || quote.status === 'expired') && (
                                            <Button variant="outline-secondary" size="sm" onClick={() => handleStatusAction(quote, 'reopen')}>
                                                {t('quotesPage.actions.reopen')}
                                            </Button>
                                        )}
                                        {canEdit && quote.status === 'accepted' && (
                                            <Button variant="success" size="sm" onClick={() => openConvert(quote)}>
                                                <FaExchangeAlt className="me-1" />
                                                {t('quotesPage.actions.convert')}
                                            </Button>
                                        )}
                                        {canDelete && quote.status !== 'converted' && (
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                title={t('quotesPage.actions.delete')}
                                                onClick={() => handleDelete(quote)}
                                            >
                                                <FaTrash />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="mt-3">
                <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                />
            </div>

            {/* Modal crear/editar */}
            <QuoteFormModal
                show={showFormModal}
                onHide={() => setShowFormModal(false)}
                onSaved={handleSaved}
                editingQuote={editingQuote}
                clientList={clientList}
                clientOptions={clientOptions}
                onClientCreated={newClient => setClientList(prev => [...prev, newClient])}
            />

            {/* Modal convertir a venta */}
            <ConvertModal
                show={showConvertModal}
                onHide={() => setShowConvertModal(false)}
                quote={convertingQuote}
                onConverted={handleConverted}
            />
        </Container>
    );
};

export default QuotesPage;
